import type { SessionContext } from "../types";

/**
 * Injects a mock session context into window.__FIREBUZZ_SESSION_CONTEXT__
 * and dispatches the firebuzz-session-ready event for consent manager polling
 */
export const injectMockSessionContext = (
	context: SessionContext,
	clearConsentState = false,
): void => {
	if (typeof window === "undefined") {
		console.warn(
			"[Mock Session] Cannot inject session context - window is undefined",
		);
		return;
	}

	// Clear consent state if requested (for fresh testing)
	if (clearConsentState) {
		localStorage.removeItem("frbzz_consent");
		console.log("[Mock Session] Cleared consent state from localStorage");
	}

	// Inject the session context
	(window as any).__FIREBUZZ_SESSION_CONTEXT__ = context;

	console.log("[Mock Session] Injected session context:", {
		userId: context.userId,
		countryCode: context.gdprSettings.countryCode,
		isEU: context.gdprSettings.isEU,
		isRequiredConsent: context.gdprSettings.isRequiredConsent,
		campaignEnvironment: context.campaignEnvironment,
	});

	// Dispatch event for consent manager polling
	window.dispatchEvent(new CustomEvent("firebuzz-session-ready"));
};

/**
 * Clears the mock session context from window
 */
export const clearMockSessionContext = (): void => {
	if (typeof window === "undefined") {
		console.warn(
			"[Mock Session] Cannot clear session context - window is undefined",
		);
		return;
	}

	delete (window as any).__FIREBUZZ_SESSION_CONTEXT__;
	console.log("[Mock Session] Cleared session context");
};

/**
 * Retrieves the current session context from window
 */
export const getMockSessionContext = (): SessionContext | null => {
	if (typeof window === "undefined") {
		return null;
	}

	return (window as any).__FIREBUZZ_SESSION_CONTEXT__ || null;
};

/**
 * Clears consent state from localStorage (useful for testing)
 */
export const clearConsentState = (): void => {
	if (typeof window === "undefined") {
		return;
	}

	localStorage.removeItem("frbzz_consent");
	console.log("[Mock Session] Cleared consent state from localStorage");
};

/**
 * Checks if a mock session context is currently active
 */
export const isMockSessionActive = (): boolean => {
	const context = getMockSessionContext();
	if (!context) return false;

	// Check if it's a mock context by looking for mock IDs
	return (
		context.userId.startsWith("mock-") ||
		context.workspaceId.startsWith("mock-") ||
		context.apiBaseUrl.includes("mock")
	);
};


